import { urlFor } from "@/utils/client";
import { ISplit } from "@/utils/types";
import dynamic from "next/dynamic";
import Image from "next/image";
import { useState } from "react";
import type { CSSProperties } from "react";
import { OutlinedButton } from "./button";
import { Pt } from "./pt";

const MuxPlayer = dynamic(() => import("@mux/mux-player-react"), {
  ssr: false,
  loading: () => <div className="aspect-video w-full bg-gray-200" />,
});

/** Two columns: content (title, rich text, buttons) next to an image or video. */
export function Split(props: ISplit) {
  const media = props.gallery?.items?.[0];
  const [playing, setPlaying] = useState(false);
  const mediaFirst = props.order === "mediaContent";

  return (
    <div className="component-padding">
      <div className="bg-white p-8 md:p-16">
        <div className="grid md:grid-cols-2 gap-8 md:gap-16 text-darkGrey">
          <div
            className={`flex flex-col items-start justify-center component-gap text-left ${
              mediaFirst ? "md:order-last" : ""
            }`}
          >
            {props.title && <p className="heading">{props.title}</p>}
            {props.description?.script && (
              <Pt data={props.description.script} />
            )}
            {props.buttons && props.buttons.length > 0 && (
              <div className="flex flex-wrap gap-4">
                {props.buttons.map((button, i) => (
                  <OutlinedButton
                    key={i}
                    link={button.link}
                    label={button.label}
                  />
                ))}
              </div>
            )}
          </div>

          {media?._type === "iwc" && media.image && (
            <div className="relative w-full aspect-video md:aspect-auto md:h-full">
              <Image
                fill
                sizes="(max-width: 768px) 100vw, 50vw"
                className="object-cover"
                src={urlFor(media.image).url()}
                alt={media.caption ?? ""}
              />
            </div>
          )}

          {media?._type === "vwc" && media.playbackId && (
            <div className="relative w-full aspect-video md:aspect-auto md:h-full overflow-hidden">
              {playing || media.autoplay ? (
                <MuxPlayer
                  streamType="on-demand"
                  playbackId={media.playbackId}
                  autoPlay
                  // Browsers only allow autoplay when muted; the CMS toggle says so.
                  muted={!!media.autoplay}
                  loop={media.loop ?? false}
                  playsInline
                  thumbnailTime={0.1}
                  disableCookies
                  className="absolute inset-0 w-full h-full"
                  style={
                    {
                      ["--media-object-fit" as never]: "cover",
                      ...(media.autoplay && media.hideControls
                        ? { ["--controls" as never]: "none" }
                        : {}),
                    } as CSSProperties
                  }
                  title={media.caption ?? ""}
                />
              ) : (
                <button
                  onClick={() => setPlaying(true)}
                  aria-label={media.caption || "Video afspelen"}
                  className="group absolute inset-0 cursor-pointer"
                >
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={`https://image.mux.com/${media.playbackId}/thumbnail.webp?time=0.1`}
                    alt={media.caption ?? ""}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  {/* same play button as the social fan cards */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-white shadow-lg flex items-center justify-center group-hover:scale-110 transition-transform group-active:scale-100">
                      <svg
                        viewBox="0 0 24 24"
                        className="w-6 h-6 md:w-7 md:h-7 fill-darkGrey ml-1"
                        aria-hidden="true"
                      >
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>
                  </div>
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
